import { getImageExtension } from '@ad/utils/image-optimizer';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AssetPickerService } from '@app/design-view/asset-picker/asset-picker.service';
import { DesignViewComponent } from '@app/design-view/design-view.component';
import { AssetPropertyContext } from '@app/design-view/properties-panel';
import {
    EditorSaveStateService,
    EditorStateService,
    ElementReplaceService
} from '@app/design-view/services';
import {
    AssetReference,
    FeededReference,
    IFeed,
    IImageElementDataNode,
    ImageSizeMode
} from '@studio/domain';
import { EventLoggerService, ImagePropertyChangeEvent } from '@studio/monitoring/browser/events';
import { createElementProperty } from '@studio/utils/element.utils';
import { Logger } from '@studio/utils/logger';
import { isSVGFile } from '@studio/utils/url';
import { uuidv4 } from '@studio/utils/utils';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ElementChangeType } from '../../services/editor-event';
import { MutatorService } from '../../services/mutator.service';

@Component({
    selector: 'image-properties',
    templateUrl: 'image-properties.component.html',
    styleUrls: ['./image-properties.component.scss']
})
export class ImagePropertiesComponent implements OnInit, OnDestroy {
    @Input() editor: DesignViewComponent;
    @Input() images$: Observable<IImageElementDataNode[]>;

    AssetPropertyContext = AssetPropertyContext;
    isSizeModeEnabled = false;
    isImageOptimizationEnabled = false;
    sizeMode: ImageSizeMode | 'mixed';
    private imageElements: IImageElementDataNode[] = [];
    private logger = new Logger('ImagePropertiesComponent');
    private unsubscribe$ = new Subject<void>();

    constructor(
        private editorState: EditorStateService,
        private mutator: MutatorService,
        private eventLogger: EventLoggerService,
        private elementReplaceService: ElementReplaceService,
        private assetPickerService: AssetPickerService,
        private editorSaveStateService: EditorSaveStateService
    ) {
        this.assetPickerService.assetSelected$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(assetEvent => {
                this.checkImageSettings([assetEvent.asset.url]);
            });
    }

    ngOnInit(): void {
        this.images$.pipe(takeUntil(this.unsubscribe$)).subscribe(images => {
            this.imageElements = images;

            const imageURLs = images.map(element => element.imageAsset?.url || '');
            this.checkImageSettings(imageURLs);
            this.refreshSizeMode();
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    updateImageSizeMode(sizeMode: ImageSizeMode): void {
        for (const element of this.imageElements) {
            this.eventLogger.log(
                new ImagePropertyChangeEvent('sizeMode', element.imageSettings.sizeMode, sizeMode)
            );
            this.mutator.setImageSettings(element, { sizeMode });
        }
        this.editor.workspace.gizmoDrawer.draw();
    }

    updateHighDPI(dpi: boolean): void {
        for (const element of this.imageElements) {
            this.eventLogger.log(
                new ImagePropertyChangeEvent('highDpi', element.imageSettings.highDpi, dpi)
            );
            this.mutator.setImageSettings(element, { highDpi: dpi });
        }
    }

    updateQuality(quality: number | undefined): void {
        for (const element of this.imageElements) {
            this.eventLogger.log(
                new ImagePropertyChangeEvent('quality', element.imageSettings.quality, quality)
            );
            this.mutator.setImageSettings(element, { quality }, ElementChangeType.Burst);
        }
    }

    onFeedSettingsChanged(event: { feed: IFeed; replaceAll: boolean }): void {
        this.replaceFeededImage(event.feed, event.replaceAll);

        if (event.replaceAll) {
            this.editorSaveStateService.save({
                saveAll: true,
                origin: 'Image properties',
                saveAndExit: false
            });
        }

        this.isImageOptimizationEnabled = true;
        this.isSizeModeEnabled = true;
    }

    private replaceFeededImage(feed: IFeed, replaceInAllDesigns: boolean): void {
        for (const element of this.imageElements) {
            this.eventLogger.log(new ImagePropertyChangeEvent('feed', element.feed, feed), this.logger);

            this.mutator.setElementPropertyValue(element, 'feed', feed);

            if (replaceInAllDesigns) {
                this.elementReplaceService.replaceFeededMediaInAllDesigns(element, feed);
            } else {
                this.elementReplaceService.replaceFeededMediaInDesign(
                    element,
                    feed,
                    this.editorState.designFork
                );
            }

            const editorElement = this.editorState.designFork.elements.find(el => el.id === element.id);
            if (editorElement) {
                const imageReference = editorElement.properties.find(
                    prop => prop.name === AssetReference.Image
                );
                const feededImageReference = editorElement.properties.find(
                    prop => prop.name === FeededReference.Image
                )!;
                const feedId = `${feed.id}.${feed.path}`;

                if (feededImageReference) {
                    feededImageReference.value = feedId;
                } else {
                    editorElement.properties.push(
                        createElementProperty({
                            clientId: uuidv4(),
                            name: FeededReference.Image,
                            unit: 'id',
                            value: feedId
                        })
                    );
                }

                if (imageReference) {
                    editorElement.properties = editorElement.properties.filter(
                        prop => prop.name !== AssetReference.Image
                    );
                }

                editorElement.parentId = undefined;
            }

            element.parentId = undefined;
        }
    }

    private checkImageSettings(imageURLs: string[]): void {
        const hasSVG = imageURLs.some((url: string) => isSVGFile(url || ''));
        const hasGIF = imageURLs.some((url: string) => getImageExtension(url) === 'gif');
        this.isImageOptimizationEnabled = !hasSVG && !hasGIF;
        this.isSizeModeEnabled = !hasSVG;
    }

    private refreshSizeMode(): void {
        if (!this.imageElements?.length) {
            return;
        }
        let sizeMode: ImageSizeMode | 'mixed' = this.imageElements[0].imageSettings.sizeMode;
        for (const image of this.imageElements) {
            if (sizeMode !== image.imageSettings.sizeMode) {
                sizeMode = 'mixed';
            }
        }
        this.sizeMode = sizeMode;
    }
}
